import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";


import {
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  ChevronRight,
  Gamepad2,
  Smartphone,
  Flame,
  Swords,
  Music2,
} from "lucide-react";

import { FaInstagram, FaYoutube } from "react-icons/fa";

import FFQADIRAZ from "../../../assets/img/ffqadiraz.png";

import {
  toggleMobileMenu,
  closeMobileMenu,
  toggleGamesDropdown,
  closeGamesDropdown,
} from "../../../redux/slices/homeSlice";

const games = [
  {
    name: "PUBG Mobile",
    description: "UC yükləmə",
    path: "/pubg",
    icon: Gamepad2,
    number: "01",
    accent: "from-purple-500 to-fuchsia-500",
  },
  {
    name: "FreeFire Diamond",
    description: "Diamond yükləmə",
    path: "/freeFire",
    icon: Flame,
    number: "02",
    accent: "from-orange-500 to-red-500",
  },
  {
    name: "Mobile Legends",
    description: "Diamond yükləmə",
    path: "/mobileLegends",
    icon: Swords,
    number: "03",
    accent: "from-cyan-400 to-blue-600",
  },
  {
    name: "TikTok Jeton",
    description: "Jeton yükləmə",
    path: "/tiktok",
    icon: Music2,
    number: "04",
    accent: "from-pink-500 to-purple-600",
  },
  {
    name: "Telefon Fanları",
    description: "Telefon aksesuarları",
    path: "/mobileAccessory",
    icon: Smartphone,
    number: "05",
    accent: "from-emerald-400 to-cyan-500",
  },
];

const navigation = [
  {
    name: "HAQQIMIZDA",
    path: "/about",
  },
  {
    name: "LOUNGE",
    path: "/lounge",
  },
  {
    name: "ƏLAQƏ",
    path: "/phone",
  },
  {
    name: "KÖMƏK",
    path: "/support",
  },
  {
    name: "ŞİKAYƏT",
    path: "/şikayət",
  },
];

const socialLinks = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/qadir.pubg/",
    icon: FaInstagram,
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@qadir.pubg?lang=en",
    icon: Music2,
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/c/FFQAD%C4%B0R",
    icon: FaYoutube,
  },
];

function Header() {
  const dispatch = useDispatch();

  const { mobileMenuOpen, gamesDropdownOpen } = useSelector(
    (state) => state.home
  );

  /* =========================================================
     THEME
  ========================================================= */

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");

    if (savedTheme === "light" || savedTheme === "dark") {
      return savedTheme;
    }

    return "dark";
  });

  const [mobileGamesOpen, setMobileGamesOpen] = useState(false);

  const gamesRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    root.setAttribute("data-theme", theme);

    localStorage.setItem("theme", theme);
  }, [theme]);

  const changeTheme = () => {
    setTheme((current) =>
      current === "dark" ? "light" : "dark"
    );
  };

  /* =========================================================
     OUTSIDE CLICK
  ========================================================= */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        gamesRef.current &&
        !gamesRef.current.contains(event.target)
      ) {
        dispatch(closeGamesDropdown());
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, [dispatch]);

  /* =========================================================
     ESC
  ========================================================= */

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        dispatch(closeGamesDropdown());
        dispatch(closeMobileMenu());
        setMobileGamesOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [dispatch]);

  /* =========================================================
     BODY SCROLL
  ========================================================= */

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setMobileGamesOpen(false);
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  /* =========================================================
     ACTIONS
  ========================================================= */

  const handleGameClick = () => {
    dispatch(closeGamesDropdown());
    dispatch(closeMobileMenu());
    setMobileGamesOpen(false);
  };

  const handleMobileMenu = () => {
    dispatch(toggleMobileMenu());
  };

  const handleCloseDrawer = () => {
    dispatch(closeMobileMenu());
    setMobileGamesOpen(false);
  };

  return (
    <>
      {/* =====================================================
          DESKTOP HEADER
      ===================================================== */}

      <header className="fixed left-0 right-0 top-0 z-[1500] hidden w-full lg:block">
        <div
          className="
            group/header
            relative
            w-full
            border-b
            border-black/[0.08]
            bg-white/[0.96]
            shadow-[0_14px_32px_rgba(0,0,0,0.20),0_5px_12px_rgba(0,0,0,0.10)]
            backdrop-blur-2xl

            dark:border-white/[0.08]
            dark:bg-[#050507]/[0.94]
            dark:shadow-[0_18px_42px_rgba(0,0,0,0.68),0_6px_16px_rgba(0,0,0,0.40)]

          "
        >
          {/* TOP GRADIENT LINE */}

          <div
            className="
              pointer-events-none
              absolute
              left-0
              right-0
              top-0
              h-px
              bg-gradient-to-r
              from-transparent
              via-fuchsia-500
              to-transparent
              opacity-70
              dark:opacity-90
            "
          />

          {/* LIGHT MODE GLOW */}

          <div
            className="
              pointer-events-none
              absolute
              -left-20
              top-0
              h-40
              w-40
              rounded-full
              bg-fuchsia-500/[0.06]
              blur-3xl

              dark:bg-fuchsia-600/10
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -bottom-20
              -right-20
              h-40
              w-40
              rounded-full
              bg-cyan-500/[0.06]
              blur-3xl

              dark:bg-cyan-500/10
            "
          />

          {/* GRID */}

          <div
            className="
              pointer-events-none
              absolute
              inset-0
              opacity-[0.025]
              [background-image:linear-gradient(rgba(0,0,0,.35)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,.35)_1px,transparent_1px)]
              [background-size:28px_28px]

              dark:opacity-[0.035]
              dark:[background-image:linear-gradient(rgba(255,255,255,.35)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.35)_1px,transparent_1px)]
            "
          />

          <div className="relative flex h-[78px] w-full items-center px-7 xl:px-10">
            {/* LOGO */}

            <Link
              to="/"
              className="group/logo relative flex shrink-0 items-center"
            >
              <div
                className="
                  absolute
                  inset-0
                  scale-75
                  rounded-full
                  bg-fuchsia-500/10
                  blur-2xl
                  transition
                  duration-500
                  group-hover/logo:scale-100

                  dark:bg-fuchsia-500/20
                "
              />

              <img
                src={FFQADIRAZ}
                alt="FF QADIR AZ"
                className="
                  relative
                  h-12
                  w-auto
                  object-contain
                  drop-shadow-[0_0_12px_rgba(217,70,239,0.20)]
                  transition
                  duration-500
                  group-hover/logo:scale-[1.04]
                  group-hover/logo:drop-shadow-[0_0_22px_rgba(217,70,239,0.55)]
                "
              />
            </Link>

            {/* divider */}

            <div
              className="
                mx-7
                h-8
                w-px
                bg-black/[0.08]

                dark:bg-white/[0.08]
              "
            />

            {/* NAVIGATION */}

            <nav className="flex h-full items-center gap-1">
              {/* OYUNLAR */}

              <div
                ref={gamesRef}
                className="relative h-full"
              >
                <button
                  type="button"
                  onClick={() =>
                    dispatch(toggleGamesDropdown())
                  }
                  className={`
                    group
                    relative
                    flex
                    h-full
                    items-center
                    gap-2
                    px-5
                    text-[11px]
                    font-black
                    tracking-[0.22em]
                    transition
                    duration-300

                    ${
                      gamesDropdownOpen
                        ? "text-slate-900 dark:text-white"
                        : "text-slate-500 hover:text-slate-900 dark:text-white/60 dark:hover:text-white"
                    }
                  `}
                >
                  <span
                    className={`
                      absolute
                      bottom-[8px]
                      left-4
                      right-4
                      h-px
                      origin-left
                      bg-gradient-to-r
                      from-fuchsia-500
                      via-purple-400
                      to-cyan-400
                      transition
                      duration-500
                      ${
                        gamesDropdownOpen
                          ? "scale-x-100 opacity-100"
                          : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
                      }
                    `}
                  />

                  <Gamepad2
                    size={17}
                    strokeWidth={1.8}
                    className={`
                      transition
                      duration-300
                      ${
                        gamesDropdownOpen
                          ? "text-fuchsia-500"
                          : "text-slate-400 group-hover:text-fuchsia-500 dark:text-white/45 dark:group-hover:text-fuchsia-400"
                      }
                    `}
                  />

                  <span>OYUNLAR</span>

                  <ChevronDown
                    size={14}
                    strokeWidth={1.8}
                    className={`
                      transition
                      duration-300
                      ${
                        gamesDropdownOpen
                          ? "rotate-180 text-fuchsia-500"
                          : "text-slate-400 dark:text-white/35"
                      }
                    `}
                  />
                </button>

                {/* DROPDOWN */}

                <div
                  className={`
                    absolute
                    left-0
                    top-[calc(100%+14px)]
                    w-[455px]
                    origin-top-left
                    transition
                    duration-300
                    ${
                      gamesDropdownOpen
                        ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                        : "pointer-events-none -translate-y-3 scale-[0.97] opacity-0"
                    }
                  `}
                >
                  <div
                    className="
                      relative
                      overflow-hidden
                      rounded-[22px]
                      border
                      border-black/[0.08]
                      bg-white
                      p-3
                      shadow-[0_35px_90px_rgba(0,0,0,0.18)]
                      backdrop-blur-3xl

                      dark:border-white/[0.09]
                      dark:bg-[#07070b]
                      dark:shadow-[0_35px_90px_rgba(0,0,0,0.7)]
                    "
                  >
                    <div
                      className="
                        pointer-events-none
                        absolute
                        -right-20
                        -top-20
                        h-56
                        w-56
                        rounded-full
                        bg-fuchsia-500/[0.06]
                        blur-3xl

                        dark:bg-fuchsia-600/10
                      "
                    />

                    <div
                      className="
                        pointer-events-none
                        absolute
                        -bottom-20
                        -left-20
                        h-56
                        w-56
                        rounded-full
                        bg-cyan-500/[0.06]
                        blur-3xl

                        dark:bg-cyan-500/10
                      "
                    />

                    <div className="relative flex items-center justify-between px-3 pb-3 pt-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-500 shadow-[0_0_10px_rgba(217,70,239,.8)]" />

                          <span className="text-[9px] font-black tracking-[0.32em] text-fuchsia-500">
                            GAME STORE
                          </span>
                        </div>

                        <p
                          className="
                            mt-1
                            text-[10px]
                            font-medium
                            tracking-[0.12em]
                            text-slate-400

                            dark:text-white/30
                          "
                        >
                          SELECT YOUR SERVICE
                        </p>
                      </div>

                      <div
                        className="
                          flex
                          items-center
                          gap-1.5
                          rounded-full
                          border
                          border-black/[0.06]
                          bg-black/[0.025]
                          px-2.5
                          py-1.5

                          dark:border-white/[0.07]
                          dark:bg-white/[0.025]
                        "
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,.8)]" />

                        <span
                          className="
                            text-[8px]
                            font-bold
                            tracking-[0.2em]
                            text-slate-400

                            dark:text-white/35
                          "
                        >
                          STORE
                        </span>
                      </div>
                    </div>

                    <div className="relative space-y-1">
                      {games.map((game) => {
                        const Icon = game.icon;

                        return (
                          <Link
                            key={game.path}
                            to={game.path}
                            onClick={handleGameClick}
                            className="
                              group/game
                              relative
                              flex
                              items-center
                              gap-3
                              overflow-hidden
                              rounded-[15px]
                              border
                              border-transparent
                              px-3
                              py-3
                              transition
                              duration-300

                              hover:border-black/[0.06]
                              hover:bg-black/[0.025]

                              dark:hover:border-white/[0.08]
                              dark:hover:bg-white/[0.045]
                            "
                          >
                            <span
                              className={`
                                pointer-events-none
                                absolute
                                bottom-2
                                left-0
                                top-2
                                w-1
                                rounded-r-full
                                bg-gradient-to-b
                                ${game.accent}
                                opacity-0
                                transition
                                duration-300
                                group-hover/game:opacity-100
                              `}
                            />

                            <span
                              className="
                                w-6
                                text-center
                                font-mono
                                text-[9px]
                                text-slate-300
                                transition
                                group-hover/game:text-slate-500

                                dark:text-white/20
                                dark:group-hover/game:text-white/50
                              "
                            >
                              {game.number}
                            </span>

                            <div
                              className={`
                                relative
                                flex
                                h-10
                                w-10
                                shrink-0
                                items-center
                                justify-center
                                overflow-hidden
                                rounded-xl
                                border
                                border-white/[0.08]
                                bg-gradient-to-br
                                ${game.accent}
                                transition
                                duration-300
                                group-hover/game:scale-105
                              `}
                            >
                              <div className="absolute inset-0 bg-black/55" />

                              <Icon
                                size={18}
                                strokeWidth={1.8}
                                className="relative text-white"
                              />
                            </div>

                            <div className="min-w-0 flex-1">
                              <p
                                className="
                                  truncate
                                  text-[12px]
                                  font-black
                                  tracking-wide
                                  text-slate-700
                                  transition
                                  group-hover/game:text-slate-950

                                  dark:text-white/80
                                  dark:group-hover/game:text-white
                                "
                              >
                                {game.name}
                              </p>

                              <p
                                className="
                                  mt-0.5
                                  text-[9px]
                                  font-medium
                                  tracking-[0.08em]
                                  text-slate-400
                                  transition
                                  group-hover/game:text-slate-500

                                  dark:text-white/25
                                  dark:group-hover/game:text-white/40
                                "
                              >
                                {game.description}
                              </p>
                            </div>

                            <ChevronRight
                              size={15}
                              strokeWidth={1.7}
                              className="
                                mr-1
                                text-slate-300
                                transition
                                duration-300
                                group-hover/game:translate-x-1
                                group-hover/game:text-fuchsia-500

                                dark:text-white/15
                                dark:group-hover/game:text-fuchsia-400
                              "
                            />
                          </Link>
                        );
                      })}
                    </div>

                    <div
                      className="
                        relative
                        mt-2
                        flex
                        items-center
                        justify-between
                        border-t
                        border-black/[0.06]
                        px-3
                        pt-3

                        dark:border-white/[0.06]
                      "
                    >
                      <span
                        className="
                          text-[8px]
                          font-bold
                          tracking-[0.2em]
                          text-slate-300

                          dark:text-white/20
                        "
                      >
                        FFQADIR AZ
                      </span>

                      <span className="text-[8px] font-bold tracking-[0.2em] text-fuchsia-500/50">
                        PREMIUM
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* NAV ITEMS */}

              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="
                    group
                    relative
                    flex
                    h-full
                    items-center
                    px-4
                    text-[11px]
                    font-black
                    tracking-[0.19em]
                    text-slate-500
                    transition
                    duration-300
                    hover:text-slate-950

                    dark:text-white/45
                    dark:hover:text-white
                  "
                >
                  <span
                    className="
                      absolute
                      bottom-[8px]
                      left-4
                      right-4
                      h-px
                      origin-left
                      scale-x-0
                      bg-gradient-to-r
                      from-fuchsia-500
                      to-cyan-400
                      opacity-0
                      transition
                      duration-300
                      group-hover:scale-x-100
                      group-hover:opacity-100
                    "
                  />

                  {item.name}
                </Link>
              ))}
            </nav>

            {/* RIGHT SIDE */}

            <div className="ml-auto flex items-center">
              <div className="mr-4 flex items-center gap-1">
                {socialLinks.map((social) => {
                  const Icon = social.icon;

                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={social.name}
                      className="
                        group/social
                        flex
                        h-9
                        w-9
                        items-center
                        justify-center
                        rounded-full
                        text-slate-400
                        transition
                        duration-300
                        hover:bg-black/[0.04]
                        hover:text-fuchsia-500

                        dark:text-white/35
                        dark:hover:bg-white/[0.05]
                        dark:hover:text-fuchsia-400
                      "
                    >
                      <Icon
                        size={16}
                        strokeWidth={1.7}
                        className="
                          transition
                          duration-300
                          group-hover/social:scale-110
                        "
                      />
                    </a>
                  );
                })}
              </div>

              <div
                className="
                  h-7
                  w-px
                  bg-black/[0.08]

                  dark:bg-white/[0.07]
                "
              />

              <button
                type="button"
                onClick={changeTheme}
                aria-label="Tema dəyiş"
                className="
                  ml-4
                  flex
                  h-9
                  w-9
                  items-center
                  justify-center
                  text-slate-500
                  transition
                  duration-300
                  hover:scale-110
                  hover:text-fuchsia-500

                  dark:text-white/45
                  dark:hover:text-fuchsia-400
                "
              >
                {theme === "dark" ? (
                  <Moon
                    size={18}
                    strokeWidth={1.7}
                  />
                ) : (
                  <Sun
                    size={19}
                    strokeWidth={1.8}
                    className="text-amber-400"
                  />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE HEADER
      ===================================================== */}

      <header className="fixed left-0 right-0 top-0 z-[1500] w-full lg:hidden">
        <div
          className="
            relative
            flex
            h-[62px]
            w-full
            items-center
            justify-between
            overflow-hidden
            border-b
            border-black/[0.08]
            bg-white/[0.96]
            px-4
            shadow-[0_12px_28px_rgba(0,0,0,0.22),0_4px_10px_rgba(0,0,0,0.11)]
            backdrop-blur-2xl

            dark:border-white/[0.08]
            dark:bg-[#050507]/90
            dark:shadow-[0_16px_36px_rgba(0,0,0,0.70),0_5px_14px_rgba(0,0,0,0.42)]

          "
        >
          {/* top line */}

          <div
            className="
              pointer-events-none
              absolute
              left-[15%]
              right-[15%]
              top-0
              h-px
              bg-gradient-to-r
              from-transparent
              via-fuchsia-500
              to-transparent
            "
          />

          <Link
            to="/"
            className="group relative flex items-center"
          >
            <div
              className="
                absolute
                inset-0
                rounded-full
                bg-fuchsia-500/10
                blur-xl

                dark:bg-fuchsia-500/15
              "
            />

            <img
              src={FFQADIRAZ}
              alt="FF QADIR AZ"
              className="
                relative
                h-10
                w-auto
                object-contain
                drop-shadow-[0_0_14px_rgba(217,70,239,0.30)]
              "
            />
          </Link>

          <button
            type="button"
            onClick={handleMobileMenu}
            aria-label="Menyu"
            className="
              relative
              flex
              h-10
              w-10
              items-center
              justify-center
              text-slate-600
              transition
              duration-300
              hover:text-slate-950

              dark:text-white/75
              dark:hover:text-white
            "
          >
            {mobileMenuOpen ? (
              <X
                size={25}
                strokeWidth={1.5}
              />
            ) : (
              <Menu
                size={25}
                strokeWidth={1.5}
              />
            )}

            <span className="absolute bottom-1 right-1 h-1 w-1 rounded-full bg-fuchsia-400 shadow-[0_0_8px_rgba(217,70,239,.9)]" />
          </button>
        </div>
      </header>

      {/* =====================================================
          MOBILE DRAWER
      ===================================================== */}

      <div
        className={`
          fixed
          inset-0
          z-[2000]
          lg:hidden
          ${
            mobileMenuOpen
              ? "pointer-events-auto"
              : "pointer-events-none"
          }
        `}
      >
        {/* backdrop */}

        <div
          onClick={handleCloseDrawer}
          className={`
            absolute
            inset-0
            bg-black/40
            backdrop-blur-md
            transition-opacity
            duration-500
            ${
              mobileMenuOpen
                ? "opacity-100"
                : "opacity-0"
            }
          `}
        />

        {/* drawer */}

        <aside
          className={`
            absolute
            right-0
            top-0
            h-full
            w-[88%]
            max-w-[430px]
            overflow-hidden
            border-l
            border-black/[0.08]
            bg-white
            shadow-[-30px_0_100px_rgba(0,0,0,0.25)]
            transition-transform
            duration-500
            ease-[cubic-bezier(.22,1,.36,1)]

            dark:border-white/[0.08]
            dark:bg-[#050507]
            dark:shadow-[-30px_0_100px_rgba(0,0,0,0.75)]

            ${
              mobileMenuOpen
                ? "translate-x-0"
                : "translate-x-full"
            }
          `}
        >
          {/* glows */}

          <div
            className="
              pointer-events-none
              absolute
              -right-24
              -top-20
              h-72
              w-72
              rounded-full
              bg-fuchsia-500/[0.06]
              blur-3xl

              dark:bg-fuchsia-600/10
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -bottom-24
              -left-24
              h-72
              w-72
              rounded-full
              bg-cyan-500/[0.06]
              blur-3xl

              dark:bg-cyan-500/10
            "
          />

          {/* left line */}

          <div
            className="
              pointer-events-none
              absolute
              bottom-0
              left-0
              top-0
              w-px
              bg-gradient-to-b
              from-transparent
              via-fuchsia-500/60
              to-transparent
            "
          />

          <div className="relative flex h-full flex-col">
            {/* DRAWER HEADER */}

            <div
              className="
                flex
                items-center
                justify-between
                border-b
                border-black/[0.07]
                px-6
                pb-5
                pt-6

                dark:border-white/[0.07]
              "
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-500 shadow-[0_0_10px_rgba(217,70,239,.8)]" />

                  <span className="text-[9px] font-black tracking-[0.35em] text-fuchsia-500">
                    FFQADIR AZ
                  </span>
                </div>

                <h2
                  className="
                    mt-2
                    text-xl
                    font-black
                    tracking-tight
                    text-slate-900

                    dark:text-white
                  "
                >
                  NAVIGATION
                </h2>
              </div>

              {/* THEME + X */}

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={changeTheme}
                  aria-label="Tema dəyiş"
                  className="
                    flex
                    h-10
                    items-center
                    justify-center
                    gap-2
                    rounded-full
                    border
                    border-black/[0.08]
                    bg-black/[0.015]
                    px-3
                    text-[9px]
                    font-black
                    tracking-[0.2em]
                    text-slate-500
                    transition
                    hover:border-black/[0.15]
                    hover:bg-black/[0.035]
                    hover:text-slate-900

                    dark:border-white/[0.08]
                    dark:bg-white/[0.025]
                    dark:text-white/45
                    dark:hover:border-white/[0.15]
                    dark:hover:bg-white/[0.04]
                    dark:hover:text-white
                  "
                >
                  {theme === "dark" ? (
                    <>
                      <Moon
                        size={14}
                        strokeWidth={1.7}
                      />

                      <span>DARK</span>
                    </>
                  ) : (
                    <>
                      <Sun
                        size={14}
                        strokeWidth={1.8}
                        className="text-amber-400"
                      />

                      <span className="text-amber-400">
                        LIGHT
                      </span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCloseDrawer}
                  aria-label="Menyunu bağla"
                  className="
                    group
                    relative
                    flex
                    h-10
                    w-10
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-black/[0.08]
                    bg-black/[0.025]
                    text-slate-500
                    transition
                    duration-300
                    hover:border-fuchsia-500/30
                    hover:bg-fuchsia-500/[0.07]
                    hover:text-fuchsia-500

                    dark:border-white/[0.09]
                    dark:bg-white/[0.025]
                    dark:text-white/50
                    dark:hover:border-fuchsia-500/30
                    dark:hover:bg-fuchsia-500/[0.08]
                    dark:hover:text-fuchsia-400
                  "
                >
                  <span
                    className="
                      pointer-events-none
                      absolute
                      inset-0
                      rounded-full
                      bg-fuchsia-500/20
                      opacity-0
                      blur-md
                      transition
                      duration-300
                      group-hover:opacity-100
                    "
                  />

                  <X
                    size={20}
                    strokeWidth={1.8}
                    className="
                      relative
                      z-10
                      transition
                      duration-300
                      group-hover:rotate-90
                    "
                  />
                </button>
              </div>
            </div>

            {/* scroll */}

            <div className="flex-1 overflow-y-auto px-5 py-6">
              {/* MOBILE GAMES */}

              <button
                type="button"
                onClick={() =>
                  setMobileGamesOpen((current) => !current)
                }
                className="
                  group
                  flex
                  w-full
                  items-center
                  justify-between
                  rounded-[17px]
                  border
                  border-black/[0.07]
                  bg-black/[0.02]
                  px-4
                  py-4
                  text-left
                  transition
                  duration-300
                  hover:border-fuchsia-500/20
                  hover:bg-black/[0.035]

                  dark:border-white/[0.07]
                  dark:bg-white/[0.025]
                  dark:hover:bg-white/[0.045]
                "
              >
                <div className="flex items-center gap-3">
                  <div
                    className="
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-xl
                      border
                      border-fuchsia-500/20
                      bg-fuchsia-500/[0.07]
                    "
                  >
                    <Gamepad2
                      size={19}
                      strokeWidth={1.7}
                      className="text-fuchsia-500"
                    />
                  </div>

                  <div>
                    <p
                      className="
                        text-[12px]
                        font-black
                        tracking-[0.15em]
                        text-slate-800

                        dark:text-white
                      "
                    >
                      OYUNLAR
                    </p>

                    <p
                      className="
                        mt-1
                        text-[9px]
                        tracking-[0.08em]
                        text-slate-400

                        dark:text-white/25
                      "
                    >
                      GAME STORE
                    </p>
                  </div>
                </div>

                <ChevronDown
                  size={17}
                  className={`
                    text-slate-400
                    transition
                    duration-300

                    dark:text-white/35

                    ${
                      mobileGamesOpen
                        ? "rotate-180 text-fuchsia-500"
                        : ""
                    }
                  `}
                />
              </button>

              {/* mobile game list */}

              <div
                className={`
                  overflow-hidden
                  transition-all
                  duration-500
                  ${
                    mobileGamesOpen
                      ? "mt-2 max-h-[500px] opacity-100"
                      : "max-h-0 opacity-0"
                  }
                `}
              >
                <div className="space-y-1 pl-2">
                  {games.map((game) => {
                    const Icon = game.icon;

                    return (
                      <Link
                        key={game.path}
                        to={game.path}
                        onClick={handleGameClick}
                        className="
                          group
                          flex
                          items-center
                          gap-3
                          rounded-[14px]
                          px-3
                          py-3
                          transition
                          duration-300
                          hover:bg-black/[0.035]

                          dark:hover:bg-white/[0.04]
                        "
                      >
                        <div
                          className={`
                            relative
                            flex
                            h-9
                            w-9
                            shrink-0
                            items-center
                            justify-center
                            overflow-hidden
                            rounded-lg
                            bg-gradient-to-br
                            ${game.accent}
                          `}
                        >
                          <div className="absolute inset-0 bg-black/55" />

                          <Icon
                            size={16}
                            className="relative text-white"
                            strokeWidth={1.8}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p
                            className="
                              truncate
                              text-[11px]
                              font-bold
                              text-slate-600
                              group-hover:text-slate-950

                              dark:text-white/70
                              dark:group-hover:text-white
                            "
                          >
                            {game.name}
                          </p>

                          <p
                            className="
                              text-[8px]
                              tracking-wider
                              text-slate-400

                              dark:text-white/25
                            "
                          >
                            {game.description}
                          </p>
                        </div>

                        <ChevronRight
                          size={14}
                          className="
                            text-slate-300
                            transition
                            group-hover:translate-x-1
                            group-hover:text-fuchsia-500

                            dark:text-white/15
                            dark:group-hover:text-fuchsia-400
                          "
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* divider */}

              <div
                className="
                  my-6
                  h-px
                  bg-gradient-to-r
                  from-transparent
                  via-black/[0.08]
                  to-transparent

                  dark:via-white/[0.08]
                "
              />

              {/* MOBILE NAVIGATION */}

              <div className="space-y-1">
                {navigation.map((item, index) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() =>
                      dispatch(closeMobileMenu())
                    }
                    className="
                      group
                      flex
                      items-center
                      justify-between
                      rounded-[14px]
                      px-4
                      py-4
                      transition
                      duration-300
                      hover:bg-black/[0.035]

                      dark:hover:bg-white/[0.04]
                    "
                    style={{
                      transitionDelay: mobileMenuOpen
                        ? `${index * 35}ms`
                        : "0ms",
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className="
                          font-mono
                          text-[8px]
                          text-slate-300

                          dark:text-white/15
                        "
                      >
                        0{index + 1}
                      </span>

                      <span
                        className="
                          text-[12px]
                          font-black
                          tracking-[0.18em]
                          text-slate-500
                          transition
                          group-hover:text-slate-950

                          dark:text-white/55
                          dark:group-hover:text-white
                        "
                      >
                        {item.name}
                      </span>
                    </div>

                    <ChevronRight
                      size={15}
                      strokeWidth={1.6}
                      className="
                        text-slate-300
                        transition
                        duration-300
                        group-hover:translate-x-1
                        group-hover:text-fuchsia-500

                        dark:text-white/15
                        dark:group-hover:text-fuchsia-400
                      "
                    />
                  </Link>
                ))}
              </div>
            </div>

            {/* SOCIAL FOOTER */}

            <div
              className="
                border-t
                border-black/[0.07]
                px-6
                py-5

                dark:border-white/[0.07]
              "
            >
              <div className="flex items-center justify-between">
                <span
                  className="
                    text-[8px]
                    font-black
                    tracking-[0.28em]
                    text-slate-300

                    dark:text-white/20
                  "
                >
                  FOLLOW US
                </span>

                <div className="flex items-center gap-2">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;

                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={social.name}
                        className="
                          flex
                          h-9
                          w-9
                          items-center
                          justify-center
                          rounded-full
                          border
                          border-black/[0.07]
                          text-slate-400
                          transition
                          hover:border-fuchsia-500/25
                          hover:bg-fuchsia-500/[0.06]
                          hover:text-fuchsia-500

                          dark:border-white/[0.07]
                          dark:text-white/35
                          dark:hover:text-fuchsia-400
                        "
                      >
                        <Icon
                          size={15}
                          strokeWidth={1.7}
                        />
                      </a>
                    );
                  })}
                </div>
              </div>

              <p
                className="
                  mt-4
                  text-[8px]
                  tracking-[0.12em]
                  text-slate-300

                  dark:text-white/15
                "
              >
                PREMIUM GAMING EXPERIENCE
              </p>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

export default Header;